import { bool } from "prop-types";
import { useRef } from "react";

import styled from "styled-components/macro";

import { useIsLoggedIn } from "Flocknote/stateHooks";

import EditRecurringGiftModalURLWatcher from "CrossFeatureComponents/UserRecurringGifts/EditRecurringGiftModalURLWatcher";
import { useManageMyGivingPage } from "Giving/ManageMyGivingPage/ManageMyGivingPageContext";

import useIsWiderThan from "Utilities/Hooks/useIsWiderThan";

import { useFetchLandingPageData } from "../useFetchLandingPageData";
import { useIsFeatureFlagEnabled } from "../useIsFeatureFlagEnabled";

import Alerts from "./Alerts";
import CallsToAction from "./CallsToAction";
import CustomMessage from "./CustomMessage";
import Footer from "./Footer";
import GiftsAndPledges from "./GiftsAndPledges";
import GivingSummary from "./GivingSummary";
import ManageMyGivingPageAuth from "./ManageMyGivingPageAuth";
import NonHeadOfHousehold from "./NonHeadOfHousehold";
import RightColumn from "./RightColumn";

const InnerContent = ({ isMobile }) => {
  const featureFlagEnabled = useIsFeatureFlagEnabled();
  const {
    state: { user, landingPageFetchStatus },
  } = useManageMyGivingPage();
  const isLoggedIn = useIsLoggedIn();
  const isHoH = user?.isHoHOrSpouseOfHoH;

  if (!featureFlagEnabled) {
    return <CustomMessage />;
  }
  if (landingPageFetchStatus === "loading") {
    return (
      <>
        <CustomMessage />
        <CallsToAction />
      </>
    );
  } else if (!isLoggedIn) {
    return (
      <>
        <CustomMessage />
        {isMobile && <ManageMyGivingPageAuth isMobile />}
        <CallsToAction />
      </>
    );
  } else if (!isHoH) {
    return (
      <>
        <CustomMessage />
        <Alerts />
        <NonHeadOfHousehold isMobile />
        <CallsToAction />
      </>
    );
  }

  InnerContent.propTypes = { isMobile: bool };

  return (
    <>
      <CustomMessage />
      <Alerts />
      {isMobile && <GivingSummary isMobile />}
      <CallsToAction />
      <GiftsAndPledges />
    </>
  );
};

export default function ManageMyGivingPageContent() {
  const bodyRef = useRef(document.body);
  const isMobile = !useIsWiderThan(bodyRef, { px: 1000 });

  const refreshLandingPageDataCall = useFetchLandingPageData();

  const handleEditRecurringModalClose = () => {
    refreshLandingPageDataCall({ hideLoading: true });
  };

  return (
    <Wrapper className="MANAGE_MY_GIVING">
      <Inside $isMobile={isMobile}>
        <PrimaryColumn className="primary_column">
          <InnerContent isMobile={isMobile} />
          <Footer />
        </PrimaryColumn>
        {!isMobile && <RightColumn />}
        <EditRecurringGiftModalURLWatcher
          onModalClose={handleEditRecurringModalClose}
        />
      </Inside>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
`;
const Inside = styled.div`
  display: ${({ $isMobile }) => ($isMobile ? "block" : "flex")};
  box-sizing: border-box;
  align-items: flex-start;
  justify-content: center;
  gap: 40px;
  position: relative;
  font-weight: 400;
  padding: 0 20px;
  width: min(100vw, 1440px);
  margin: 0 auto;
  position: relative;
  z-index: 5;
  @media (max-width: 375px) {
    padding: 10px;
  }
`;

const PrimaryColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex-grow: 1;
  container-type: inline-size;
  container-name: primary_column;
  padding-bottom: 46px;
`;
